import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Paper,
} from "@mui/material";

// Import all your 9 logos here
import forestryLogo from "../../../images/collabs/fwfd.png";
import wildlifeKpLogo from "../../../images/collabs/kpwd.png";
import lumsLogo from "../../../images/collabs/lumsOwn.png";
import margallaLogo from "../../../images/collabs/mhnp.png";
import natGeoLogo from "../../../images/collabs/natgeo.png";
import pdoLogo from "../../../images/collabs/pdo.png";
import parksGilgitLogo from "../../../images/collabs/pwdgb.png";
import skidoLogo from "../../../images/collabs/skido.png";
import wwfLogo from "../../../images/collabs/wwf.png";

export default function Teams() {
  // ================================================
  // 1. OUR TEAM (2 logos)
  // ================================================
  const teamMembers = [
    {
      name: "Lahore University of Management Sciences",
      logo: lumsLogo,
    },
    {
      name: "World WildLife Fund",
      logo: wwfLogo,
    },
  ];

  // ================================================
  // 2. OUR COLLABORATORS (7 logos)
  // ================================================
  const collaborators = [
    {
      name: "Forestry, Wildlife & Fisheries Department",
      logo: forestryLogo,
    },
    {
      name: "Wildlife Department Khyber Pakhtunkhwa",
      logo: wildlifeKpLogo,
    },
    {
      name: "Margalla Hills National Park",
      logo: margallaLogo,
    },
    {
      name: "National Geographic",
      logo: natGeoLogo,
    },
    {
      name: "Passu Development Organization",
      logo: pdoLogo,
    },
    {
      name: "Parks & Wildlife Gilgit Baltistan",
      logo: parksGilgitLogo,
    },
    {
      name: "Shahi Khyber Imamabad Development Organization",
      logo: skidoLogo,
    },
  ];

  // ================================================
  // COMPACT GLASSMORPHIC FLIP TILE STYLES
  // ================================================
  const tileWrapperStyle = {
    perspective: "1000px",
    width: "100%",
    maxWidth: 140, // ✅ Reduced from 180
    height: 140, // ✅ Reduced from 180
    mx: "auto",
  };

  const tileInnerStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    transition: "transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)",
    transformStyle: "preserve-3d",
    "&:hover": {
      transform: "rotateY(180deg)",
    },
  };

  const tileFaceStyle = {
    position: "absolute",
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    p: 1, // ✅ Reduced from 2 (removed internal padding)
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    transition: "box-shadow 0.3s ease, border-color 0.3s ease",
    "&:hover": {
      boxShadow: "0 16px 48px rgba(0, 0, 0, 0.15)",
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
  };

  const frontFaceStyle = {
    ...tileFaceStyle,
    zIndex: 2,
  };

  const backFaceStyle = {
    ...tileFaceStyle,
    transform: "rotateY(180deg)",
    zIndex: 1,
  };

  // ================================================
  // COMPACT LOGO STYLES
  // ================================================
  const logoContainerStyle = {
    width: "100%",
    height: 60, // ✅ Reduced from 80
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 0.5, // ✅ Reduced from 1
    px: 0.5,
  };

  const logoImageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  };

  // ================================================
  // RENDER TILE COMPONENT
  // ================================================
  const renderTile = (item, index) => (
    <Grid item xs={6} sm={4} md={3} key={index}>
      <Box sx={tileWrapperStyle}>
        <Box sx={tileInnerStyle}>
          {/* FRONT: Logo only */}
          <Paper elevation={0} sx={frontFaceStyle}>
            <Box sx={logoContainerStyle}>
              <Box
                component="img"
                src={item.logo}
                alt={item.name}
                sx={logoImageStyle}
              />
            </Box>
          </Paper>

          {/* BACK: Full Name (handles 2-3 lines automatically) */}
          <Paper elevation={0} sx={backFaceStyle}>
            <Typography
              variant="caption" // ✅ Smallest text
              fontWeight={700}
              sx={{
                color: "#333",
                textAlign: "center",
                lineHeight: 1.2,
                wordBreak: "break-word",
                px: 0.5,
                fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem" },
              }}
            >
              {item.name}
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Grid>
  );

  return (
    <Box
      sx={{
        // py: 2, // ✅ Reduced from 4
        background: "linear-gradient(180deg, #1a1a1a 0%, #636363 100%)",
        position: "relative",
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
      }}
      id="teams"
    >
      <Container maxWidth="xl">
        {/* ============================================ */}
        {/* SECTION 1: OUR TEAM (2 Tiles) */}
        {/* ============================================ */}
        <Box sx={{ mb: 2 }}>
          {" "}
          {/* ✅ Reduced from 4 */}
          <Typography
            variant="h5" // ✅ Reduced from h4
            fontWeight={700}
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 1,
              color: "#fff",
              fontSize: { xs: "1.4rem", md: "2rem" },
            }}
          >
            Our Team
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              mb: 2, // ✅ Reduced from 3
              color: "rgba(255,255,255,0.7)",
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "0.75rem", md: "0.85rem" },
            }}
          >
            Leading organizations driving wildlife conservation and research
          </Typography>
          <Grid container spacing={1} justifyContent="center">
            {" "}
            {/* ✅ spacing={1} */}
            {teamMembers.map(renderTile)}
          </Grid>
        </Box>

        <Divider
          sx={{
            mb: 2, // ✅ Reduced from 4
            borderColor: "rgba(255,255,255,0.08)",
          }}
        />

        {/* ============================================ */}
        {/* SECTION 2: OUR COLLABORATORS (7 Tiles) */}
        {/* ============================================ */}
        <Box>
          <Typography
            variant="h5" // ✅ Reduced from h4
            fontWeight={700}
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 1,
              color: "#fff",
              fontSize: { xs: "1.4rem", md: "2rem" },
            }}
          >
            Our Collaborators
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              mb: 2, // ✅ Reduced from 3
              color: "rgba(255,255,255,0.7)",
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "0.75rem", md: "0.85rem" },
            }}
          >
            Partnering with leading organizations for wildlife conservation
          </Typography>

          <Grid container spacing={1} justifyContent="center">
            {" "}
            {/* ✅ spacing={1} */}
            {collaborators.map(renderTile)}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
